/**
 * The paths a group can be told to skip, and how to label them.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Options come from the live preview, which is the only source that speaks both
 * ids at once: it walks the real flow AND carries each path's bank row id (the
 * stable thing an ignore list stores). A path the bank has no row for simply
 * has no `pathBankId` and cannot be offered — you cannot name a row the bank has
 * never heard of.
 */

import type { FlowOptimizerItemPreview } from '@trafficpulse/shared';

export interface IgnorePathOption {
  value: string;
  label: string;
}

/** Path rows the bank knows, deduped by bank id, sorted by name. */
export function ignorePathOptions(
  items: ReadonlyArray<FlowOptimizerItemPreview> | undefined,
): IgnorePathOption[] {
  const byBankId = new Map<string, string>();
  for (const item of items ?? []) {
    if (item.layer !== 'paths' || !item.pathBankId) continue;
    if (!byBankId.has(item.pathBankId)) {
      byBankId.set(item.pathBankId, item.itemName || item.itemId);
    }
  }
  return [...byBankId]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Ids that are stored but not on the flow right now (the path was removed, or
 * the bank has not been pulled). They are KEPT, never quietly dropped: the path
 * can come back, and discarding the user's choice at the moment it stops being
 * visible is how a setting silently un-sets itself.
 */
export function offFlowIds(
  value: ReadonlyArray<string>,
  options: ReadonlyArray<IgnorePathOption>,
): string[] {
  const known = new Set(options.map((o) => o.value));
  return value.filter((id) => !known.has(id));
}
